import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import styles from './journey-explorer.module.scss';
import { usePageTracking } from '../../../hooks/usePageTracking';
import { useJourneyExplorerApiService } from '../../../api/providers/AppServiceProvider';
import {
	ChurnInsightsResult,
	FirstDays,
	InteractionFilterMode,
	JourneyAttribute,
	JourneyCompare,
	JourneyExploreResult,
	JourneyFilterState,
	JourneyFlowResult,
	JourneyMeta,
	SavedViewState,
	TransitionFilterMode,
} from '../../../models/journey-explorer/journey-explorer';
import {
	CHURN_INSIGHTS_ENABLED,
	ExplorerView,
	FilterPill,
	FlowSettings,
	flowScope,
	MapMode,
	buildPills,
	GoalInfo,
	errorMessage,
	flowWindowLabel,
	goalOf,
	setInteractionFilter,
	setTransitionFilter,
	withCustomerValue,
	withGoal,
} from './journey-explorer-utils';
import { EMPTY_SIMPLIFY, isSimplified, removeGroup, simplifyMeta, viewName } from './journey-simplify';
import {
	allInteractionNames,
	defaultFilterState,
	showingStages,
	stageGroups,
	suggestedPercents,
	withStages,
	withoutStages,
} from './journey-funnel';
import JourneyFunnelView from './components/journey-funnel-view';
import JourneyStepsView from './components/journey-steps-view';
import { CompareData } from './journey-compare';
import { IJourneyExplorerApiService } from '../../../api/services/journey-explorer-api-service';
import { InteractionInfo, buildInteractionLookup, resolveInfo } from './journey-map-layout';
import { keepAttributeValue } from './journey-segments';
import { RequestLoader, useDebouncedRequest } from './use-debounced-request';
import Sidebar from './components/sidebar';
import ActiveFiltersBar from './components/active-filters-bar';
import KpiBar from './components/kpi-bar';
import ViewTabs from './components/view-tabs';
import MapControls from './components/map-controls';
import JourneyMap from './components/journey-map';
import JourneyFlow from './components/journey-flow';
import JourneyPaths from './components/journey-paths';
import ExploreDrawer, { DrawerView } from './components/explore-drawer';
import CompareControl from './components/compare-control';
import { LoadingBar } from './components/loading-indicator';
import SavedViews from './components/saved-views';
import { HistoryButtons } from './components/view-history';
import {
	clearJourneyCache,
	readJourneyCache,
	sameFilters,
	saveCachedMeta,
	saveCachedSnapshot,
} from './journey-explorer-cache';
import {
	EMPTY_HISTORY,
	VIEW_PARAM,
	ViewHistory,
	decodeViewState,
	parseViewState,
	recordHistory,
	stepHistory,
	toViewState,
} from './journey-view-state';
import { JourneyApiContext, JourneySource, useJourneyApi } from './journey-api-context';
import { SubscriptionDemoApiService } from './journey-subscription-demo';

const META_ERROR = 'Journey Explorer could not be loaded.';
const EXPLORE_ERROR = 'The journey data could not be loaded.';
const FLOW_ERROR = 'The flow could not be loaded.';
const INSIGHTS_ERROR = 'Churn insights could not be loaded.';
const REFRESH_ERROR = 'The data could not be refreshed.';
const MAP_DESCRIPTION = 'Every interaction and transition at once';
const FLOW_DESCRIPTION = 'What customers do before, after or between interactions';
const FUNNEL_DESCRIPTION = 'Where customers drop off along the most common route';
const STEPS_DESCRIPTION = 'Every step side by side: reach, goal rate, drop-off and timing';
const HISTORY_BUTTONS_ENABLED = false;
const SIDEBAR_COLLAPSED_KEY = 'journeyExplorer.sidebarCollapsed';
const SOURCE_KEY = 'journeyExplorer.source';
const GOAL_KEY = 'journeyExplorer.goal';
const HISTORY_SETTLE_MS = 600;

interface ExploreSnapshot {
	filters: JourneyFilterState;
	data: JourneyExploreResult;
}

interface CompareSnapshot {
	spec: JourneyCompare;
	data: JourneyExploreResult;
}

type ExploreApi = { current: Pick<IJourneyExplorerApiService, 'explore'> };

const compareLoader = (
	api: ExploreApi,
	filters: JourneyFilterState | null,
	compare: JourneyCompare | null,
	side: 'a' | 'b'
): RequestLoader<CompareSnapshot> =>
	filters && compare
		? (signal) =>
				api.current
					.explore(withCustomerValue(filters, compare.column, compare[side]), signal)
					.then((data) => ({ spec: compare, data }))
		: null;

const takeLinkedView = (): SavedViewState | null => {
	const url = new URL(window.location.href);
	const text = url.searchParams.get(VIEW_PARAM);
	if (!text) {
		return null;
	}
	url.searchParams.delete(VIEW_PARAM);
	window.history.replaceState(window.history.state, '', url.toString());
	return decodeViewState(text);
};

interface JourneyExplorerViewProps {
	source: JourneySource;
	onSourceChange: (source: JourneySource) => void;
}

const readGoal = (key: string): string | null | undefined => {
	const stored = localStorage.getItem(key);
	return stored === null ? undefined : stored || null;
};

const JourneyExplorerView: FC<JourneyExplorerViewProps> = ({ source, onSourceChange }) => {
	const goalKey = `${GOAL_KEY}.${source}`;
	const apiService = useJourneyApi();
	const apiRef = useRef(apiService);
	apiRef.current = apiService;
	const { accounts } = useMsal();
	const accountScope = accounts[0]?.homeAccountId || accounts[0]?.localAccountId || 'default';
	const cacheScope = source === 'website' ? accountScope : `${accountScope}:${source}`;
	const [cache, setCache] = useState(() => readJourneyCache(cacheScope));
	const [refreshing, setRefreshing] = useState(false);

	const [meta, setMeta] = useState<JourneyMeta | null>(null);
	const [metaError, setMetaError] = useState<string | null>(null);
	const [metaAttempt, setMetaAttempt] = useState(0);
	const [filterState, setFilterState] = useState<JourneyFilterState | null>(null);
	const [exploreAttempt, setExploreAttempt] = useState(0);
	const [flowAttempt, setFlowAttempt] = useState(0);
	const [view, setView] = useState<ExplorerView>('map');
	const [mapMode, setMapMode] = useState<MapMode>('customers');
	const [interactionsPct, setInteractionsPct] = useState(100);
	const [transitionsPct, setTransitionsPct] = useState(85);
	const [flowSettings, setFlowSettings] = useState<FlowSettings>({
		direction: 'after',
		anchor: null,
		end: null,
		steps: 3,
		within: 'any',
	});
	const [firstDays, setFirstDays] = useState<FirstDays | null>(60);
	const [sidebarCollapsed, setSidebarCollapsed] = useState(
		() => localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === 'true'
	);

	const [drawer, setDrawer] = useState<DrawerView[]>([]);
	const [compare, setCompare] = useState<JourneyCompare | null>(null);

	const toggleSidebar = useCallback(() => setSidebarCollapsed((collapsed) => !collapsed), []);
	const openDrawer = useCallback((view: DrawerView) => setDrawer([view]), []);
	const pushDrawer = useCallback((view: DrawerView) => setDrawer((views) => [...views, view]), []);
	const backDrawer = useCallback(() => setDrawer((views) => views.slice(0, -1)), []);
	const closeDrawer = useCallback(() => setDrawer([]), []);
	const openCustomer = useCallback((customerId: string) => setDrawer([{ mode: 'timeline', customerId }]), []);
	const keepValue = useCallback(
		(attribute: JourneyAttribute, value: string) =>
			setFilterState((current) => (current ? keepAttributeValue(current, attribute, value) : current)),
		[]
	);

	useEffect(() => localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(sidebarCollapsed)), [sidebarCollapsed]);

	const applyViewState = useCallback((state: SavedViewState) => {
		setFilterState((current) =>
			state.filters.goal === undefined ? { ...state.filters, goal: current?.goal ?? null } : state.filters
		);
		setFlowSettings(state.flow);
		setView(state.view);
		setCompare(state.compare);
		setDrawer([]);
	}, []);

	const filterStateRef = useRef(filterState);
	filterStateRef.current = filterState;
	const defaultFilters = useRef<JourneyFilterState | null>(null);

	// Fresh meta only replaces the filters while they are still the untouched defaults.
	const receiveMeta = useCallback(
		(response: JourneyMeta) => {
			setMeta(response);
			const current = filterStateRef.current;
			if (!current) {
				const linked = takeLinkedView();
				if (linked) {
					const goal =
						linked.filters.goal === undefined
							? defaultFilterState(response, readGoal(goalKey)).goal
							: linked.filters.goal;
					applyViewState({ ...linked, filters: { ...linked.filters, goal } });
					return;
				}
			} else if (!sameFilters(current, defaultFilters.current)) {
				return;
			}
			const next = defaultFilterState(response, readGoal(goalKey));
			if (!sameFilters(next, current)) {
				defaultFilters.current = next;
				filterStateRef.current = next;
				setFilterState(next);
			}
		},
		[applyViewState, goalKey]
	);

	const cachedMeta = useRef(cache?.meta ?? null);
	useEffect(() => {
		if (cachedMeta.current) {
			receiveMeta(cachedMeta.current);
			cachedMeta.current = null;
		}
	}, [receiveMeta]);

	useEffect(() => {
		let active = true;
		setMetaError(null);
		apiRef.current
			.getMeta()
			.then((response) => {
				if (active) {
					receiveMeta(response);
					saveCachedMeta(cacheScope, response);
				}
			})
			.catch((error) => active && setMetaError(errorMessage(error, META_ERROR)));
		return () => {
			active = false;
		};
	}, [metaAttempt, receiveMeta, cacheScope]);

	const hardRefresh = useCallback(async () => {
		setRefreshing(true);
		try {
			await apiRef.current.refresh();
			clearJourneyCache(cacheScope);
			setCache(null);
			setMetaAttempt((attempt) => attempt + 1);
			setExploreAttempt((attempt) => attempt + 1);
			setFlowAttempt((attempt) => attempt + 1);
		} catch (error) {
			setMetaError(errorMessage(error, REFRESH_ERROR));
		} finally {
			setRefreshing(false);
		}
	}, [cacheScope]);

	const flowOpen = view === 'flow';
	const { anchor, end, direction, steps, within } = flowSettings;
	const between = direction === 'between';
	const scope = useMemo(() => flowScope(flowSettings), [flowSettings]);
	const scopedFilters = useMemo(
		() => (filterState && scope ? { ...filterState, scope } : filterState),
		[filterState, scope]
	);

	const loadExplore = useMemo<RequestLoader<ExploreSnapshot>>(
		() =>
			scopedFilters
				? (signal) =>
						apiRef.current.explore(scopedFilters, signal).then((data) => ({ filters: scopedFilters, data }))
				: null,
		[scopedFilters]
	);
	const loadCompareA = useMemo(() => compareLoader(apiRef, scopedFilters, compare, 'a'), [scopedFilters, compare]);
	const loadCompareB = useMemo(() => compareLoader(apiRef, scopedFilters, compare, 'b'), [scopedFilters, compare]);
	const loadFlow = useMemo<RequestLoader<JourneyFlowResult>>(
		() =>
			flowOpen && filterState && anchor && (!between || end)
				? (signal) =>
						apiRef.current.getFlow(
							{
								filters: filterState,
								direction,
								anchor,
								steps,
								within,
								compare,
								...(between && { end }),
							},
							signal
						)
				: null,
		[flowOpen, filterState, direction, between, anchor, end, steps, within, compare]
	);
	const loadInsights = useMemo<RequestLoader<ChurnInsightsResult>>(
		() =>
			CHURN_INSIGHTS_ENABLED && flowOpen && filterState
				? (signal) => apiRef.current.getChurnInsights({ filters: filterState, first_days: firstDays }, signal)
				: null,
		[flowOpen, filterState, firstDays]
	);

	const explore = useDebouncedRequest(loadExplore, EXPLORE_ERROR, exploreAttempt);
	const flow = useDebouncedRequest(loadFlow, FLOW_ERROR, flowAttempt);
	const insights = useDebouncedRequest(loadInsights, INSIGHTS_ERROR, flowAttempt);
	const compareA = useDebouncedRequest(loadCompareA, EXPLORE_ERROR, exploreAttempt);
	const compareB = useDebouncedRequest(loadCompareB, EXPLORE_ERROR, exploreAttempt);

	const compareData = useMemo<CompareData | null>(() => {
		if (!compare) {
			return null;
		}
		const sideResult = (snapshot: CompareSnapshot | null) => (snapshot?.spec === compare ? snapshot.data : null);
		return {
			spec: compare,
			a: { label: compare.a, result: sideResult(compareA.data) },
			b: { label: compare.b, result: sideResult(compareB.data) },
		};
	}, [compare, compareA.data, compareB.data]);
	const compareLabels = useMemo<[string, string] | null>(() => (compare ? [compare.a, compare.b] : null), [compare]);
	const cachedSnapshot =
		cache?.snapshot && sameFilters(cache.snapshot.filters, scopedFilters) ? cache.snapshot : null;
	const snapshot = explore.data ?? cachedSnapshot;
	const result = snapshot?.data ?? null;

	useEffect(() => {
		if (explore.data) {
			saveCachedSnapshot(cacheScope, explore.data);
		}
	}, [explore.data, cacheScope]);
	const simplifyState = filterState?.simplify;
	const simplify = useMemo(() => simplifyState ?? EMPTY_SIMPLIFY, [simplifyState]);
	const viewMeta = useMemo(() => (meta ? simplifyMeta(meta, simplify) : null), [meta, simplify]);
	const viewNames = useMemo(
		() =>
			new Set(
				viewMeta?.interaction_types.flatMap((type) =>
					type.interactions.map((interaction) => interaction.name)
				) ?? []
			),
		[viewMeta]
	);
	const lookup = useMemo(() => {
		const all = meta ? buildInteractionLookup(meta) : new Map<string, InteractionInfo>();
		if (viewMeta && viewMeta !== meta) {
			buildInteractionLookup(viewMeta).forEach((info, name) => {
				if (!all.has(name)) {
					all.set(name, { ...info, order: all.size });
				}
			});
		}
		return all;
	}, [meta, viewMeta]);
	const pills = useMemo(() => (meta && filterState ? buildPills(filterState, meta) : []), [meta, filterState]);
	const goal = goalOf(filterState);
	const goalView = goal ? viewName(simplify, goal) ?? goal : null;
	const goalLabel = goalView ? resolveInfo(lookup, goalView).label : null;
	const goalInfo = useMemo<GoalInfo | null>(
		() =>
			goalView && goalLabel
				? {
						name: goalView,
						label: goalLabel,
						rate: result?.kpis.goal_rate_pct ?? 0,
						outcome: filterState?.goal?.outcome ?? null,
				  }
				: null,
		[goalView, goalLabel, result, filterState]
	);
	const flowError = flowOpen ? flow.error ?? insights.error : null;
	const compareError = compare ? compareA.error ?? compareB.error : null;
	const exploreLoading = explore.loading || (compare !== null && (compareA.loading || compareB.loading));
	const busy = exploreLoading || (flowOpen && (flow.loading || insights.loading));
	const bannerMessage = metaError ?? explore.error ?? compareError ?? flowError;

	const previousSimplify = useRef(simplify);
	useEffect(() => {
		const before = previousSimplify.current;
		previousSimplify.current = simplify;
		if (before === simplify) {
			return;
		}
		const remap = (name: string | null) => {
			if (name === null) {
				return null;
			}
			const raw = before.groups.find((group) => group.label === name)?.names[0] ?? name;
			return viewName(simplify, raw);
		};
		setFlowSettings((current) => ({ ...current, anchor: remap(current.anchor), end: remap(current.end) }));
	}, [simplify]);

	useEffect(() => {
		if (!result || (anchor && (!between || (end && end !== anchor)))) {
			return;
		}
		const ranked = [...result.interactions]
			.filter((interaction) => viewNames.has(interaction.name))
			.sort((a, b) => b.customers - a.customers)
			.map((interaction) => interaction.name);
		const nextAnchor =
			anchor ?? ranked.find((name) => name !== goalView) ?? viewNames.values().next().value ?? null;
		const nextEnd =
			between && (!end || end === nextAnchor)
				? (goalView && goalView !== nextAnchor && viewNames.has(goalView) ? goalView : null) ??
				  ranked.find((name) => name !== nextAnchor) ??
				  null
				: end;
		if (nextAnchor !== anchor || nextEnd !== end) {
			setFlowSettings((current) => ({ ...current, anchor: nextAnchor, end: nextEnd }));
		}
	}, [anchor, end, between, result, goalView, viewNames]);

	const viewState = useMemo(
		() => (filterState ? toViewState(filterState, flowSettings, view, compare) : null),
		[filterState, flowSettings, view, compare]
	);
	const viewJson = viewState?.flow.anchor ? JSON.stringify(viewState) : null;
	const [history, setHistory] = useState<ViewHistory>(EMPTY_HISTORY);
	useEffect(() => {
		if (!viewJson) {
			return;
		}
		const timer = setTimeout(() => setHistory((current) => recordHistory(current, viewJson)), HISTORY_SETTLE_MS);
		return () => clearTimeout(timer);
	}, [viewJson]);
	const moveHistory = (delta: number) => {
		const next = stepHistory(history, delta);
		const state = next === history ? null : parseViewState(JSON.parse(next.entries[next.index]));
		if (state) {
			setHistory(next);
			applyViewState(state);
		}
	};

	const removePill = (pill: FilterPill) => setFilterState((current) => (current ? pill.remove(current) : current));
	const clearAll = () => meta && setFilterState(defaultFilterState(meta, readGoal(goalKey)));
	const retry = () => {
		if (metaError) {
			setMetaAttempt((attempt) => attempt + 1);
		} else if (explore.error || compareError) {
			setExploreAttempt((attempt) => attempt + 1);
		} else {
			setFlowAttempt((attempt) => attempt + 1);
		}
	};

	const changeFlow = useCallback(
		(patch: Partial<FlowSettings>) => {
			setFlowSettings((current) => ({ ...current, ...patch }));
			if (!(patch.applyToAll ?? flowSettings.applyToAll)) {
				setView('flow');
			}
		},
		[flowSettings.applyToAll]
	);

	const addInteractionFilter = useCallback(
		(name: string, mode: InteractionFilterMode) =>
			setFilterState((current) => (current ? setInteractionFilter(current, name, mode) : current)),
		[]
	);

	const addTransitionFilter = useCallback(
		(from: string, to: string, mode: TransitionFilterMode) =>
			setFilterState((current) => (current ? setTransitionFilter(current, from, to, mode) : current)),
		[]
	);

	const autoSliders = useRef(true);
	useEffect(() => {
		if (result && autoSliders.current) {
			const suggested = suggestedPercents(result);
			setInteractionsPct(suggested.interactions);
			setTransitionsPct(suggested.transitions);
		}
	}, [result]);
	const changeInteractionsPct = useCallback((value: number) => {
		autoSliders.current = false;
		setInteractionsPct(value);
	}, []);
	const changeTransitionsPct = useCallback((value: number) => {
		autoSliders.current = false;
		setTransitionsPct(value);
	}, []);

	const stages = meta && stageGroups(meta).length > 0 ? showingStages(simplify, meta) : null;
	const changeStages = (on: boolean) => {
		if (!meta) {
			return;
		}
		autoSliders.current = true;
		setFilterState((current) => {
			if (!current) {
				return current;
			}
			const next = on ? withStages(simplify, meta) : withoutStages(simplify, meta);
			return { ...current, simplify: isSimplified(next) ? next : undefined };
		});
	};
	const expandGroup = useCallback((groupLabel: string) => {
		autoSliders.current = true;
		setFilterState((current) => (current ? removeGroup(current, groupLabel) : current));
	}, []);

	const goalOptions = useMemo(() => (meta ? allInteractionNames(meta) : []), [meta]);
	const changeGoal = useCallback(
		(next: string | null) => {
			localStorage.setItem(goalKey, next ?? '');
			setFilterState((current) => (current ? withGoal(current, next) : current));
		},
		[goalKey]
	);

	const label = (name: string) => resolveInfo(lookup, name).label;
	const withinText = within === 'any' ? '' : `, within ${flowWindowLabel(within)}`;
	let flowDescription = FLOW_DESCRIPTION;
	if (anchor && between && end) {
		flowDescription = `What customers do between ${label(anchor)} and ${label(
			end
		)}, over up to ${steps} interactions${withinText}`;
	} else if (anchor && !between) {
		flowDescription = `What customers do ${direction} ${label(anchor)}, ${steps} interactions ${
			direction === 'after' ? 'ahead' : 'back'
		}${withinText}`;
	}
	const scopeNote = scope ? `. Only counting ${flowDescription.replace(/^What /, 'what ')}` : '';
	const descriptions = {
		map: MAP_DESCRIPTION + scopeNote,
		flow: flowDescription,
		funnel: FUNNEL_DESCRIPTION + scopeNote,
		steps: STEPS_DESCRIPTION + scopeNote,
	};

	return (
		<div className={styles.page}>
			{meta && viewMeta && filterState ? (
				<Sidebar
					meta={meta}
					viewMeta={viewMeta}
					lookup={lookup}
					filterState={filterState}
					flowActive={flowOpen || scope !== null}
					flowSettings={flowSettings}
					collapsed={sidebarCollapsed}
					filterCount={pills.length}
					onFilterChange={setFilterState}
					onFlowChange={changeFlow}
					onToggleCollapsed={toggleSidebar}
					refreshing={refreshing}
					onRefresh={hardRefresh}
					source={source}
					onSourceChange={onSourceChange}
					goalOptions={goalOptions}
					onGoalChange={changeGoal}
					onResetFilters={clearAll}
				/>
			) : (
				<aside className={styles.sidebar} />
			)}
			<section className={styles.main}>
				<LoadingBar active={busy} />
				<KpiBar
					kpis={result?.kpis ?? null}
					loading={exploreLoading}
					compare={compareData}
					goalLabel={goalLabel}
				/>
				{bannerMessage && (
					<div className={styles.banner} role="alert">
						{bannerMessage}
						<button type="button" className={styles.retry} onClick={retry}>
							Retry
						</button>
					</div>
				)}
				<div className={styles.main_scroll}>
					<ViewTabs
						view={view}
						descriptions={descriptions}
						onChange={setView}
						leading={
							HISTORY_BUTTONS_ENABLED && (
								<HistoryButtons
									canBack={history.index > 0}
									canForward={history.index < history.entries.length - 1}
									onBack={() => moveHistory(-1)}
									onForward={() => moveHistory(1)}
								/>
							)
						}
					>
						{view === 'map' && (
							<MapControls
								mapMode={mapMode}
								interactionsPct={interactionsPct}
								transitionsPct={transitionsPct}
								stages={stages}
								onMapModeChange={setMapMode}
								onInteractionsPctChange={changeInteractionsPct}
								onTransitionsPctChange={changeTransitionsPct}
								onStagesChange={changeStages}
							/>
						)}
						<div className={styles.view_actions}>
							{meta && <CompareControl meta={meta} compare={compare} onChange={setCompare} />}
							<SavedViews current={viewState} onOpen={applyViewState} />
						</div>
					</ViewTabs>
					{pills.length > 0 && (
						<ActiveFiltersBar
							matchCount={result ? result.kpis.customers : null}
							pills={pills}
							onRemove={removePill}
							onClearAll={clearAll}
						/>
					)}
					<div className={view === 'map' ? styles.view : styles.view_hidden}>
						{viewMeta && filterState ? (
							<JourneyMap
								meta={viewMeta}
								lookup={lookup}
								result={result}
								filterState={scopedFilters ?? filterState}
								mode={mapMode}
								interactionsPct={interactionsPct}
								transitionsPct={transitionsPct}
								loading={exploreLoading}
								onInteractionFilter={addInteractionFilter}
								onTransitionFilter={addTransitionFilter}
								onExplore={openDrawer}
								onExpandGroup={expandGroup}
								compare={compareData}
							/>
						) : (
							<div className={styles.canvas} />
						)}
					</div>
					{view === 'funnel' && (
						<JourneyFunnelView
							result={result}
							lookup={lookup}
							simplify={simplify}
							goal={goal}
							goalOptions={goalOptions}
							loading={exploreLoading}
							onGoalChange={changeGoal}
							onStoppedHere={(name) => addInteractionFilter(name, 'ended_here')}
						/>
					)}
					{view === 'steps' && viewMeta && (
						<JourneyStepsView
							result={result}
							meta={viewMeta}
							lookup={lookup}
							simplify={simplify}
							goalLabel={goalLabel}
							goalView={goalView}
							interactionsPct={interactionsPct}
							loading={exploreLoading}
							onInteractionFilter={addInteractionFilter}
							onExplore={openDrawer}
						/>
					)}
					{flowOpen && meta && filterState && (
						<JourneyFlow
							meta={meta}
							filters={filterState}
							lookup={lookup}
							flow={flow.data}
							insights={insights.data}
							interactions={result?.interactions ?? []}
							goal={goalInfo}
							settings={flowSettings}
							firstDays={firstDays}
							currency={meta?.currency ?? 'GBP'}
							loading={flow.loading}
							insightsLoading={insights.loading}
							onFlowChange={changeFlow}
							onFirstDaysChange={setFirstDays}
							onOpenDrawer={openDrawer}
							onKeep={keepValue}
							compareLabels={compareLabels}
						/>
					)}
				</div>
				<JourneyPaths
					paths={result?.paths ?? null}
					filters={snapshot?.filters ?? null}
					lookup={lookup}
					loading={explore.loading && result !== null}
					onOpenCustomer={openCustomer}
					compare={compareData}
				/>
			</section>
			{meta && filterState && drawer.length > 0 && (
				<ExploreDrawer
					views={drawer}
					meta={meta}
					filters={flowOpen ? filterState : scopedFilters ?? filterState}
					lookup={lookup}
					onPush={pushDrawer}
					onBack={backDrawer}
					onClose={closeDrawer}
					onKeep={keepValue}
				/>
			)}
		</div>
	);
};

const JourneyExplorerPage: FC = () => {
	usePageTracking('Page Viewed', 'Journey Explorer');
	const realApi = useJourneyExplorerApiService();
	const realApiRef = useRef(realApi);
	realApiRef.current = realApi;
	const [source, setSource] = useState<JourneySource>(() =>
		localStorage.getItem(SOURCE_KEY) === 'subscription' ? 'subscription' : 'website'
	);
	const demoApi = useMemo(() => new SubscriptionDemoApiService(() => realApiRef.current), []);

	useEffect(() => localStorage.setItem(SOURCE_KEY, source), [source]);

	return (
		<JourneyApiContext.Provider value={source === 'subscription' ? demoApi : null}>
			<JourneyExplorerView key={source} source={source} onSourceChange={setSource} />
		</JourneyApiContext.Provider>
	);
};

export default JourneyExplorerPage;
